import React, { useEffect, useRef, useState } from 'react';
import Typography from '@digital-hig/mui/dist/Typography';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import produce from 'immer';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';

// eslint-disable-next-line import/no-extraneous-dependencies
import SvgIcon from '@mui/material/SvgIcon';
import SvgError from '@digital-hig/icon/lib/esm/display-icons/error';
import SvgComplete from '@digital-hig/icon/lib/esm/display-icons/complete';
import Waffle from '@digital-hig/icon/lib/esm/icons/waffle';

const QuizTypeOrdering = ({
  question, quizState, setQuizState, makingOrderingProbAsAnswered, isSignedIn,
}) => {
  // for mobile
  let movingElement;
  let movingEleWidth;
  let movingEleHeight;
  let currentDropId;
  let updatedMobileQuizState;

  const [dragging, setDragging] = useState(false);
  const [curDragTo, setCurDragTo] = useState(-1);
  const dragItem = useRef();
  const dragNode = useRef();
  const draggedFrom = useRef();
  const touchRef = useRef({});

  // Ordering problem should be as answered on load itself as it does already have some order
  useEffect(() => {
    makingOrderingProbAsAnswered(quizState);
  }, []);

  const displayErrorOrSuccess = () => {
    if (Object.hasOwnProperty.call(question, 'isCorrect')) {
      if (!question.isCorrect) {
        return (
          <Grid className="answer-error">
            <SvgIcon size="medium"><SvgError /></SvgIcon>
            <Typography component="span" variant="body-copy-small" color="ink-error">
              {`Incorrect. ${question.explanation}`}
            </Typography>
          </Grid>
        );
      }
      return (
        <Grid className="answer-success">
          <SvgIcon size="medium"><SvgComplete /></SvgIcon>
          <Typography component="span" variant="body-copy-small">
            Nice work
          </Typography>
        </Grid>
      );
    }

    return <></>;
  };

  const handleDragOver = (e, group) => {
    const evt = e;
    evt.preventDefault();
    if (evt.target.innerHTML === '' && group === 'premise') {
      evt.target.classList.add('dragOverBg');
    }
  };

  const handleDrop = (e, orderingQuestion) => {
    e.stopPropagation();
    if (!quizState.showReset && isSignedIn) {
      const ansClone = [...orderingQuestion.answers];
      const dragFrom = orderingQuestion.answers.findIndex((ans) => ans.id === dragItem.current);
      const dragTo = orderingQuestion.answers.findIndex((ans) => ans.id === e.target.id);

      if (dragTo !== -1 && curDragTo !== e.target.id) {
        setCurDragTo(e.target.id);
        ansClone.splice(dragTo, 0, ansClone.splice(dragFrom, 1)[0]);
        const updatedQuizState = produce(quizState, (draftState) => {
          if (draftState.questions) {
            draftState.questions.forEach((questionn) => {
              const q = questionn;
              if (q.id === orderingQuestion.id) {
                q.answers = ansClone;
              }
            });
          }
        });
        setQuizState(updatedQuizState);
        makingOrderingProbAsAnswered(updatedQuizState);
      }
    }
  };

  const handleDragEnd = (e, group) => {
    setDragging(false);
    setCurDragTo(-1);
    if (group === 'premise') {
      e.target.parentNode.classList.add('dropped');
    }
    if (dragNode.current) {
      dragNode.current.removeEventListener('dragend', handleDragEnd);
    }
    dragItem.current = null;
    dragNode.current = null;
  };

  const handleOnDragStart = (e, dragId, groupFrom = '') => {
    if (!quizState.showReset) {
      const evt = e;
      evt.target.classList.add('dragStartBg');
      dragItem.current = dragId;
      dragNode.current = e.target;
      draggedFrom.current = groupFrom;
      evt.target.parentNode.classList.remove('dropped');
      dragNode.current.addEventListener('dragend', (ev) => { handleDragEnd(ev, groupFrom); });
      setTimeout(() => setDragging(true), 0);
    }
  };

  const handleTouchStart = (e, dragId) => {
    dragNode.current = touchRef?.current[dragId];
    movingElement = dragNode?.current?.cloneNode(true);
    movingElement?.classList?.add('dragged-item');
    const dragItemBoundings = dragNode?.current?.getBoundingClientRect();
    movingEleWidth = dragItemBoundings?.width - 30;
    movingEleHeight = dragItemBoundings?.height - 26;
  };

  const handleTouchMove = (e, dragId, orderingQuestion) => {
    const y = e?.changedTouches[0]?.pageY;
    movingElement.style.position = 'absolute';
    movingElement.style.top = `${y - dragNode?.current.offsetHeight / 2}px`;
    movingElement.style.left = '34px';
    movingElement.style.width = `${movingEleWidth}px`;
    movingElement.style.height = `${movingEleHeight}px`;
    movingElement.style.opacity = '0.8';

    dragNode?.current?.classList?.add('hide');
    dragNode?.current?.parentNode?.appendChild(movingElement);

    Object.keys(touchRef.current).forEach((dropId) => {
      if (dropId !== dragId) {
        if ((movingElement?.offsetTop + movingElement?.offsetHeight / 2) < (touchRef?.current[dropId].offsetTop + touchRef.current[dropId].offsetHeight)
        && ((movingElement?.offsetTop + movingElement?.offsetHeight) - movingElement?.offsetHeight / 2) > touchRef.current[dropId].offsetTop) {
          touchRef.current[dropId].firstElementChild.classList.add('hide');
          touchRef.current[dropId].classList.add('bg');

          currentDropId = dropId;

          const ansClone = [...orderingQuestion.answers];
          const dragFrom = orderingQuestion?.answers.findIndex((ans) => ans.id === dragId);
          const dragTo = orderingQuestion?.answers.findIndex((ans) => ans.id === dropId);

          ansClone.splice(dragTo, 0, ansClone.splice(dragFrom, 1)[0]);
          updatedMobileQuizState = produce(quizState, (draftState) => {
            if (draftState.questions) {
              draftState.questions.forEach((que) => {
                const q = que;
                if (q.id === orderingQuestion.id) {
                  q.answers = ansClone;
                }
              });
            }
          });
        } else {
          touchRef?.current[dropId]?.firstElementChild?.classList.remove('hide');
          touchRef?.current[dropId]?.classList.remove('bg');
        }
      }
    });
  };

  const handleTouchEnd = () => {
    if (touchRef.current[currentDropId]
        && movingElement
        && (((movingElement?.offsetTop + movingElement?.offsetHeight / 2) < touchRef?.current[currentDropId].offsetTop)
        || movingElement?.offsetTop > (touchRef.current[currentDropId]?.offsetTop + touchRef?.current[currentDropId].offsetHeight / 2))) {
      touchRef?.current[currentDropId]?.firstElementChild?.classList?.remove('hide');
      touchRef?.current[currentDropId]?.classList?.remove('bg');
      setQuizState(quizState);
      updatedMobileQuizState = undefined;
    } else if (updatedMobileQuizState) {
      touchRef?.current[currentDropId]?.firstElementChild?.classList?.remove('hide');
      touchRef?.current[currentDropId]?.classList?.remove('bg');
      setQuizState(updatedMobileQuizState);
    } else {
      touchRef?.current[currentDropId]?.firstElementChild?.classList?.remove('hide');
      touchRef?.current[currentDropId]?.classList?.remove('bg');
    }
    movingElement?.remove();
    dragNode?.current?.classList?.remove('hide');
  };
  return (
    <RootElement>
      <Grid id={`qid${question.id}`} container flexDirection="column" key={question.id}>
        <Grid item lg={6} md={9}>
          <Grid className="question-title">
            <Typography component="h4" variant="header-copy-small">{question.text}</Typography>
          </Grid>
          <Grid className="ordering-problem" style={{ postion: 'relative' }}>
            {question.answers.map((answer) => (
              <Grid
                className="item-container"
                onDragEnter={(e) => (dragging ? handleDrop(e, question) : null)}
              >
                <div ref={(element) => { touchRef.current[answer.id] = element; }}>
                  <Grid
                    className={dragging ? `${(answer.id === dragItem.current) ? 'bg' : ''} item` : 'item'}
                    draggable={!!(isSignedIn)}
                    onDragOver={handleDragOver}
                    onDragStart={(e) => handleOnDragStart(e, answer.id)}
                    onDrop={(e) => handleDrop(e, question)}
                    onTouchStart={(e) => handleTouchStart(e, answer.id)}
                    onTouchMove={(e) => handleTouchMove(e, answer.id, question)}
                    onTouchEnd={(e) => handleTouchEnd(e, answer.id)}
                    id={answer.id}
                  >
                    <Grid className={dragging ? `${(answer.id === dragItem.current) ? 'hide item-data' : 'item-data'}` : 'item-data'}>
                      <SvgIcon size="medium"><Waffle /></SvgIcon>
                      <div className="answer-text">{answer.text}</div>
                    </Grid>
                  </Grid>
                </div>

              </Grid>
            ))}
            {displayErrorOrSuccess()}
          </Grid>
        </Grid>
      </Grid>
    </RootElement>
  );
};

const RootElement = styled.div`
margin-top: 30px;
.question-title {
  margin-bottom: 22px;
}
.ordering-problem, .response-container{
  display: flex;
  flex-direction: column;
  flex: 1;

  .item-container{
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      background-color: white;
  .item {
      flex: 1;
      padding: 22px;
      border: 1px solid rgba(204, 204, 204, 0.25);
      border-radius: 6px;
      box-shadow: 0 1px 0px rgba(102, 102, 102, 0.8);
      background-color: #ffffff;
      touch-action: none;
      user-select: none;

      &[draggable="false"]{
        opacity: 0.4;
      }
      .answer-text {
          margin-left: 20px;
          font-size: 14px;
      }
      :hover {
          background: radial-gradient(65.63% 63.74% at 91.94% 0%, rgba(128, 128, 255, 0.15) 0%, rgba(128, 128, 255, 0) 100%);
          border: solid 2px #8080ff;
          box-shadow: none;
          box-sizing: border-box;
          padding: 21px;
        }
      .item-data {
          display: flex;
          align-items: center;
      }
    }
  }
  .bg {
      background-color: #f2f2f2 !important;
      box-shadow: none !important;
      border: 1px solid #f2f2f2 !important;
  }
  
}
.dropped {
  border: none !important;

  .item {
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1) !important;
  }
}
.dragOverBg {
  background-color: #f2f2f2;
}
.dragStartBg {
  background-color: #ffffff;
}
`;

QuizTypeOrdering.propTypes = {
  question: PropTypes.arrayOf({}).isRequired,
  makingOrderingProbAsAnswered: PropTypes.func.isRequired,
  isSignedIn: PropTypes.bool.isRequired,
  quizState: PropTypes.shape({
    showReset: PropTypes.bool,
    unansweredQuestions: PropTypes.arrayOf(),
    showUnanswered: PropTypes.string,
  }).isRequired,
  setQuizState: PropTypes.func.isRequired,
};

export default React.memo(QuizTypeOrdering);
